/**
 * Public customer-facing page reached at `/share/:token`. No auth.
 * Shows a clean approved-stamp project summary and gives the customer
 * one-click downloads for the closeout PDF and (if available) the raw
 * ZIP. Renders friendly errors for expired/revoked/404 links.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Archive, ShieldCheck, MapPin, Calendar, AlertCircle, Loader2 } from "lucide-react";
import { projectTypeLabel } from "@/lib/constants";

function formatBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1_048_576) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1_073_741_824) return `${(n / 1_048_576).toFixed(1)} MB`;
  return `${(n / 1_073_741_824).toFixed(2)} GB`;
}

export default function SharePage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API_BASE}/share/${token}`)
      .then((r) => alive && setState({ loading: false, data: r.data, error: null }))
      .catch((e) => {
        if (!alive) return;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        setState({
          loading: false,
          data: null,
          error: { status, message: detail || "We couldn't load this share link." },
        });
      });
    return () => { alive = false; };
  }, [token]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6">
        <Card className="bg-slate-900 border-slate-800 max-w-md w-full" data-testid="share-error-card">
          <div className="brand-bar" />
          <CardContent className="p-6 text-center space-y-3">
            <AlertCircle className="w-10 h-10 text-amber-400 mx-auto" />
            <h1 className="font-display font-black text-xl tracking-tight">
              {state.error.status === 410 ? "Link unavailable" : "Link not found"}
            </h1>
            <p className="text-sm text-slate-400">{state.error.message}</p>
            <p className="text-xs text-slate-500">
              If you still need this closeout package, please contact your Force2 project manager.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { project, available, expires_at: expiresAt } = state.data;
  const expDate = (() => { try { return new Date(expiresAt); } catch { return null; } })();
  const isApproved = project.status === "approved" || project.status === "closed";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="share-page">
      <header className="border-b border-slate-800 bg-slate-950">
        <div className="brand-bar" />
        <div className="max-w-3xl mx-auto p-5 flex items-center gap-3">
          <img src="/force2-logo.png" alt="Force2 Telecom" className="w-12 h-12 object-contain rounded-md bg-slate-900 border border-slate-800" />
          <div className="leading-tight">
            <div className="font-display font-black text-xl tracking-tight">FORCE2 TELECOM</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-slate-400">Project Closeout Package</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-5 sm:p-8 space-y-5">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-slate-400 font-display font-bold">Site</div>
                <div className="font-mono text-blue-400 text-sm mt-0.5" data-testid="share-site-number">{project.site_number}</div>
                <h1 className="font-display font-black text-3xl tracking-tight mt-1" data-testid="share-site-name">{project.site_name}</h1>
                <div className="text-sm text-slate-400 mt-1">{project.customer} · {projectTypeLabel(project.project_type)}</div>
              </div>
              {isApproved && (
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border-2 border-emerald-700 bg-emerald-950/50 text-emerald-300"
                  data-testid="share-approved-stamp"
                >
                  <ShieldCheck className="w-4 h-4" />
                  <span className="font-display font-black tracking-widest text-xs uppercase">Approved</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {project.address && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Address</div>
                    <div>{project.address}</div>
                  </div>
                </div>
              )}
              {project.project_date && (
                <div className="flex items-start gap-2">
                  <Calendar className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Date</div>
                    <div>{project.project_date}</div>
                  </div>
                </div>
              )}
              {project.market && (
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Market</div>
                  <div>{project.market}</div>
                </div>
              )}
              {project.foreman && (
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Foreman</div>
                  <div>{project.foreman}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <div className="font-display font-bold tracking-tight">Downloads</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                asChild
                disabled={!available.pdf}
                className="bg-red-700 hover:bg-red-600 font-display tracking-wider h-auto py-4"
                data-testid="share-download-pdf"
              >
                <a href={`${API_BASE}/share/${token}/closeout.pdf`}>
                  <FileText className="w-5 h-5 mr-2" />
                  <div className="flex flex-col items-start">
                    <span>CLOSEOUT PDF</span>
                    <span className="text-[10px] font-normal opacity-80 normal-case tracking-normal">
                      {available.pdf ? formatBytes(available.pdf_size) : "Not yet available"}
                    </span>
                  </div>
                </a>
              </Button>
              <Button
                asChild
                disabled={!available.zip}
                variant="outline"
                className="border-slate-700 bg-slate-900 hover:bg-slate-800 font-display tracking-wider h-auto py-4"
                data-testid="share-download-zip"
              >
                <a href={`${API_BASE}/share/${token}/raw.zip`}>
                  <Archive className="w-5 h-5 mr-2 text-emerald-400" />
                  <div className="flex flex-col items-start">
                    <span>RAW FILES ZIP</span>
                    <span className="text-[10px] font-normal opacity-80 normal-case tracking-normal">
                      {available.zip ? formatBytes(available.zip_size) : "Not yet available"}
                    </span>
                  </div>
                </a>
              </Button>
            </div>
            {(!available.pdf && !available.zip) && (
              <div className="text-xs text-amber-400">
                The closeout package is still being prepared. Please check back shortly.
              </div>
            )}
            {expDate && (
              <div className="text-[11px] text-slate-500">
                Link expires {expDate.toLocaleDateString()} at {expDate.toLocaleTimeString()}.
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center text-[11px] text-slate-600 pt-4">
          Force2 Telecom · FieldOps Platform
        </div>
      </main>
    </div>
  );
}
